<template>
  <div class="weekly-view-discipline w-full max-w-full min-w-0">
    <div class="weekly-view-discipline__filters">
      <span class="inline-flex items-center gap-1 mr-1 text-xs font-bold uppercase text-gray-500">
        <b-icon pack="fas" icon="eye" size="is-small" />
        {{ $t('groups') }}
      </span>
      <button
        v-for="group in groupFilters"
        :key="group.key"
        type="button"
        class="activities-weekly-table__group-chip inline-flex items-center gap-1.5 py-0.5 px-2 border-0 rounded-full text-white text-xs font-bold cursor-pointer"
        :class="{ 'activities-weekly-table__group-chip--hidden': isGroupHidden(group.columnGroup) }"
        :style="{ backgroundColor: group.color }"
        :aria-pressed="isGroupHidden(group.columnGroup)"
        @click="toggleColumnGroup(group.columnGroup)"
      >
        {{ $t(group.labelKey) }}
        <span class="inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-white text-gray-800 text-xs font-bold">{{ group.count }}</span>
      </button>
    </div>

    <div v-if="isLoading" class="py-8 text-center text-sm text-gray-500">
      {{ $t('loading') }}...
    </div>

    <div v-else-if="!disciplines.length" class="py-8 text-center text-sm text-gray-500">
      {{ $t('no_results') }}
    </div>

    <div v-else class="weekly-view-discipline__list">
      <div
        v-for="discipline in disciplines"
        :key="discipline.id"
        class="weekly-view-discipline__card"
      >
        <div class="weekly-view-discipline__card-inner">
          <span
            class="weekly-view-discipline__accent"
            :style="{ backgroundColor: disciplineColor(discipline) }"
          />

          <div class="weekly-view-discipline__card-body">
            <div
              class="weekly-view-discipline__row"
              @click="toggleDiscipline(discipline)"
            >
              <button
                type="button"
                class="weekly-view-discipline__expand-btn"
                :aria-expanded="isExpanded(discipline)"
                @click.stop="toggleDiscipline(discipline)"
              >
                <b-icon
                  pack="fas"
                  :icon="isExpanded(discipline) ? 'chevron-down' : 'chevron-right'"
                  size="is-small"
                />
              </button>

              <span class="weekly-view-discipline__type-badge">
                {{ $t('discipline') }}
              </span>

              <span
                class="weekly-view-discipline__name-badge"
                :style="{ backgroundColor: disciplineColor(discipline) }"
              >
                {{ discipline.name }}
              </span>

              <span class="weekly-view-discipline__counts">
                {{ disciplineCountsLabel(discipline) }}
              </span>

              <span
                v-if="disciplineProgressLabel(discipline)"
                class="weekly-view-discipline__progress"
                :style="{ color: disciplineColor(discipline) }"
              >
                {{ disciplineProgressLabel(discipline) }}
              </span>
            </div>

            <div
              v-if="isExpanded(discipline)"
              class="weekly-view-discipline__panel"
            >
              <div
                v-if="!hasDisciplineActivities(discipline)"
                class="weekly-view-discipline__empty"
              >
                {{ $t('no_activities_for_discipline') }}
              </div>
              <activities-weekly-table
                v-else
                :key="'discipline-activities-' + discipline.id"
                :project-id="projectId"
                :group-id="discipline.id"
                :discipline-mode="true"
                activities-load-mode="group"
                embedded
                :week-start-date="weekStartDate"
                :week-end-date="weekEndDate"
                :week-number="weekNumber"
                :hidden-column-groups="hiddenColumnGroups"
                :filter-subactivities-by-week="filterSubactivitiesByWeek"
                :reschedule-mode="rescheduleMode"
                :edited-subactivities="editedSubactivities"
                :activity-filters="activityFilters"
                @update-rescheduled-activity="$emit('update-rescheduled-activity', $event)"
                @remove-rescheduled-activity="$emit('remove-rescheduled-activity', $event)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ActivitiesWeeklyTable from './activities-weekly-table.vue'

const DEFAULT_DISCIPLINE_COLOR = '#3b66bc'

export default {
  name: 'weekly-view-discipline',

  components: {
    ActivitiesWeeklyTable,
  },

  props: {
    projectId: {
      type: Number,
      default: null,
    },
    weekStartDate: {
      type: String,
      default: null,
    },
    weekEndDate: {
      type: String,
      default: null,
    },
    weekNumber: {
      type: Number,
      default: null,
    },
    filterSubactivitiesByWeek: {
      type: Boolean,
      default: true,
    },
    rescheduleMode: {
      type: Boolean,
      default: false,
    },
    editedSubactivities: {
      type: Array,
      default: () => [],
    },
    activityFilters: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      isLoading: false,
      disciplines: [],
      expandedDisciplineIds: [],
      hiddenColumnGroups: [],
      groupFilters: [
        { key: 'info', columnGroup: 'tracking', labelKey: 'information', color: '#3b82f6', count: 13 },
        { key: 'planned_advance', columnGroup: 'planned_advance', labelKey: 'planned_advance', color: '#1e3a5f', count: 6 },
        { key: 'scheduled_advance', columnGroup: 'scheduled_advance', labelKey: 'scheduled_advance', color: '#166534', count: 6 },
        { key: 'real', columnGroup: 'real', labelKey: 'real_progress', color: '#7c3aed', count: 5 },
      ],
    }
  },

  watch: {
    projectId: {
      immediate: true,
      handler() {
        this.loadDisciplines()
      },
    },
    weekStartDate() {
      this.loadDisciplines()
    },
    weekEndDate() {
      this.loadDisciplines()
    },
    filterSubactivitiesByWeek() {
      this.loadDisciplines()
    },
  },

  methods: {
    // activityFilters solo aplican a activities-weekly-table, no a la lista de disciplinas.
    buildRequestParams() {
      const params = {
        only_disciplines: 1,
        with_groups: 1,
        p: this.projectId,
        order_field: 'planning_code',
        order_dir: 'asc',
      }

      if (this.filterSubactivitiesByWeek && this.weekStartDate && this.weekEndDate) {
        params.weekly_start = this.weekStartDate
        params.weekly_end = this.weekEndDate
      }

      return params
    },

    async loadDisciplines() {
      if (!this.projectId) {
        this.disciplines = []
        return
      }

      this.isLoading = true

      try {
        const { data } = await axios.get(`/api/v2/project/${this.projectId}/activity/groups`, {
          params: this.buildRequestParams(),
        })

        this.disciplines = data && data.groups ? data.groups : []
      } catch (error) {
        this.disciplines = []
        this.$notify.error('error_loading_activities')
      } finally {
        this.isLoading = false
      }
    },

    disciplineColor(discipline) {
      if (discipline.color) {
        return discipline.color
      }

      if (discipline.configs) {
        try {
          const configs = typeof discipline.configs === 'string'
            ? JSON.parse(discipline.configs)
            : discipline.configs

          if (configs && configs.color) {
            return configs.color
          }
        } catch (error) {
          return DEFAULT_DISCIPLINE_COLOR
        }
      }

      return DEFAULT_DISCIPLINE_COLOR
    },

    disciplineCountsLabel(discipline) {
      return this.$t('activities_subactivities_count', {
        activities: Number(discipline.activities_count || 0),
        subactivities: Number(discipline.sub_activities_count || 0),
      })
    },

    disciplineProgressLabel(discipline) {
      const subCount = Number(discipline.sub_activities_count || 0)
      const progress = discipline.sub_activities_avg_progress

      if (subCount === 0 || progress == null) {
        return null
      }

      return this.$t('average_progress', {
        value: Math.round(Number(progress)),
      })
    },

    hasDisciplineActivities(discipline) {
      return Number(discipline.sub_activities_count || 0) > 0
        || Number(discipline.activities_count || 0) > 0
    },

    isExpanded(discipline) {
      return this.expandedDisciplineIds.includes(discipline.id)
    },

    toggleDiscipline(discipline) {
      if (this.isExpanded(discipline)) {
        this.expandedDisciplineIds = this.expandedDisciplineIds.filter((id) => id !== discipline.id)
        return
      }

      this.expandedDisciplineIds = [...this.expandedDisciplineIds, discipline.id]
    },

    isGroupHidden(groupKey) {
      return this.hiddenColumnGroups.includes(groupKey)
    },

    toggleColumnGroup(groupKey) {
      if (this.isGroupHidden(groupKey)) {
        this.hiddenColumnGroups = this.hiddenColumnGroups.filter((key) => key !== groupKey)
        return
      }

      this.hiddenColumnGroups = [...this.hiddenColumnGroups, groupKey]
    },
  },
}
</script>

<style scoped>
.activities-weekly-table__group-chip--hidden {
  opacity: 0.45;
}

.weekly-view-discipline__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 1rem;
  margin-bottom: 0.75rem;
  border: 1px solid #c5d4eb;
  border-radius: 0.5rem;
  background-color: #fff;
}

.weekly-view-discipline__list {
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.weekly-view-discipline__card {
  min-width: 0;
  border: 1px solid #dbe6f5;
  border-radius: 0.5rem;
  background-color: #fff;
  overflow: hidden;
}

.weekly-view-discipline__card-inner {
  display: flex;
  min-width: 0;
}

.weekly-view-discipline__accent {
  width: 4px;
  flex-shrink: 0;
  align-self: stretch;
}

.weekly-view-discipline__card-body {
  flex: 1;
  min-width: 0;
}

.weekly-view-discipline__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 2.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
}

.weekly-view-discipline__expand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  padding: 0;
  border: 0;
  background: transparent;
  color: #6b7280;
}

.weekly-view-discipline__type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
  background-color: #4b5563;
  color: #fff;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
  flex-shrink: 0;
}

.weekly-view-discipline__name-badge {
  display: inline-flex;
  align-items: center;
  max-width: 14rem;
  padding: 0.125rem 0.625rem;
  border-radius: 9999px;
  color: #fff;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}

.weekly-view-discipline__counts {
  flex: 1;
  min-width: 0;
  font-size: 0.75rem;
  color: #374151;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.weekly-view-discipline__progress {
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
}

.weekly-view-discipline__panel {
  border-top: 1px solid #dbe6f5;
  background-color: #fff;
}

.weekly-view-discipline__empty {
  padding: 1.5rem 0.75rem;
  text-align: center;
  font-size: 0.8125rem;
  color: #6b7280;
}
</style>
